/**
 * guide.tsx — Guide d'utilisation Harmonia
 * Chapitres accordéon animés — thème dark
 * Contenu : interface utilisateur uniquement (pas de technique)
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  StyleSheet, Text, View, ScrollView, TouchableOpacity,
  Animated, Platform, SafeAreaView,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

const NATIVE = Platform.OS !== 'web';

// ─── Palette ──────────────────────────────────────────────────────────────────
const DARK_BG     = '#0D0D1A';
const CARD_BG     = '#14142B';
const CARD_BORDER = '#2A2A4A';
const TEXT_MAIN   = '#F0EFF8';
const TEXT_SUB    = '#9491B4';
const BRAND_GOLD  = '#FFD700';
const PURPLE      = '#7B1FA2';
const PURPLE_DARK = '#4B0082';
const ACCENT      = '#7B5FFF';
const SUCCESS     = '#00C853';

// ─── Types ────────────────────────────────────────────────────────────────────
interface Step {
  title: string;
  desc:  string;
  pro?:  boolean;
}

interface Chapter {
  id:       string;
  title:    string;
  icon:     string;
  color:    string;
  gradient: [string, string];
  steps:    Step[];
}

// ─── Métadonnées des chapitres (icônes, couleurs, structure — non traduisibles) ─
interface ChapterMeta {
  id:        string;
  icon:      string;
  color:     string;
  gradient:  [string, string];
  stepCount: number;
  proSteps:  number[]; // index (0-based) des étapes marquées PRO
}

const CHAPTERS_META: ChapterMeta[] = [
  { id: 'compte',   icon: 'person-circle-outline',   color: ACCENT,      gradient: ['#3B1FA2', '#7B5FFF'], stepCount: 6,  proSteps: [] },
  { id: 'actu',     icon: 'newspaper-outline',       color: PURPLE,      gradient: ['#4B0082', '#7B1FA2'], stepCount: 17, proSteps: [5] },
  { id: 'jeux',     icon: 'game-controller-outline', color: '#A78BFA',   gradient: ['#4C1D95', '#8A2BE2'], stepCount: 16, proSteps: [] },
  { id: 'messages', icon: 'chatbubbles-outline',     color: '#8A2BE2',   gradient: ['#2E1050', '#8A2BE2'], stepCount: 15, proSteps: [8, 9] },
  { id: 'amis',     icon: 'people-outline',          color: '#E040FB',   gradient: ['#4A0072', '#9C27B0'], stepCount: 5,  proSteps: [] },
  { id: 'profil',   icon: 'person-outline',          color: SUCCESS,     gradient: ['#004D2A', '#00C853'], stepCount: 10, proSteps: [8] },
  { id: 'pro',      icon: 'star-outline',            color: BRAND_GOLD,  gradient: ['#3A2000', '#FF8C00'], stepCount: 5,  proSteps: [] },
  { id: 'market',   icon: 'storefront-outline',      color: BRAND_GOLD,  gradient: ['#3A2000', '#FF8C00'], stepCount: 9,  proSteps: [4, 5, 6] },
];

function getChapters(t: (key: string) => string): Chapter[] {
  return CHAPTERS_META.map(meta => {
    const base = `guideScreen.chapters.${meta.id}`;
    const steps: Step[] = Array.from({ length: meta.stepCount }, (_, i) => ({
      title: t(`${base}.steps.step${i + 1}.title`),
      desc:  t(`${base}.steps.step${i + 1}.desc`),
      pro:   meta.proSteps.includes(i) || undefined,
    }));
    return {
      id:       meta.id,
      title:    t(`${base}.title`),
      icon:     meta.icon,
      color:    meta.color,
      gradient: meta.gradient,
      steps,
    };
  });
}

// ─── Composant AnimCard ───────────────────────────────────────────────────────
interface AnimCardProps {
  chapter:  Chapter;
  isOpen:   boolean;
  onToggle: () => void;
  t:        (key: string) => string;
}

function AnimCard({ chapter, isOpen, onToggle, t }: AnimCardProps) {
  const rotateAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(rotateAnim, {
      toValue:         isOpen ? 1 : 0,
      duration:        260,
      useNativeDriver: NATIVE,
    }).start();
  }, [isOpen]);

  const rotate = rotateAnim.interpolate({
    inputRange:  [0, 1],
    outputRange: ['0deg', '180deg'],
  });

  return (
    <View style={[gS.card, { borderColor: chapter.color + '44' }]}>

      {/* ── En-tête ── */}
      <TouchableOpacity style={gS.cardHeader} onPress={onToggle} activeOpacity={0.75}>
        <LinearGradient
          colors={chapter.gradient}
          style={gS.chapterIconBox}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
        >
          <Ionicons name={chapter.icon as any} size={21} color="#fff" />
        </LinearGradient>

        <Text style={gS.chapterTitle} numberOfLines={1}>{chapter.title}</Text>

        <View style={[gS.stepCount, { backgroundColor: chapter.color + '22', borderColor: chapter.color + '44' }]}>
          <Text style={[gS.stepCountText, { color: chapter.color }]}>{chapter.steps.length}</Text>
        </View>

        <Animated.View style={{ transform: [{ rotate }] }}>
          <Ionicons name="chevron-down" size={20} color={TEXT_SUB} />
        </Animated.View>
      </TouchableOpacity>

      {/* ── Contenu ── */}
      {isOpen && (
        <View style={gS.stepsWrap}>
          {chapter.steps.map((step, i) => (
            <View key={i} style={gS.stepRow}>

              {/* Timeline */}
              <View style={gS.timelineCol}>
                <View style={[gS.timelineDot, { backgroundColor: chapter.color }]}>
                  <Text style={gS.timelineNum}>{i + 1}</Text>
                </View>
                {i < chapter.steps.length - 1 && (
                  <View style={[gS.timelineLine, { backgroundColor: chapter.color + '33' }]} />
                )}
              </View>

              {/* Contenu de l'étape */}
              <View style={gS.stepContent}>
                <View style={gS.stepTitleRow}>
                  <Text style={gS.stepTitle}>{step.title}</Text>
                  {step.pro && (
                    <View style={gS.proBadge}>
                      <Ionicons name="star" size={9} color={BRAND_GOLD} />
                      <Text style={gS.proBadgeText}>PRO</Text>
                    </View>
                  )}
                </View>
                <Text style={gS.stepDesc}>{step.desc}</Text>
              </View>

            </View>
          ))}
        </View>
      )}
    </View>
  );
}

// ─── Écran principal ──────────────────────────────────────────────────────────
export default function GuideScreen() {
  const router  = useRouter();
  const { t }   = useTranslation();
  const CHAPTERS = getChapters(t);
  const [openId, setOpenId] = useState<string | null>(null);

  const toggle = (id: string) =>
    setOpenId(prev => (prev === id ? null : id));

  return (
    <SafeAreaView style={gS.root}>

      {/* ── Header ── */}
      <LinearGradient
        colors={[PURPLE_DARK, PURPLE]}
        style={gS.header}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
      >
        <TouchableOpacity style={gS.backBtn} onPress={() => router.back()} activeOpacity={0.75}>
          <Ionicons name="arrow-back" size={22} color="#fff" />
        </TouchableOpacity>
        <View style={gS.headerCenter}>
          <View style={gS.headerIconBox}>
            <MaterialCommunityIcons name="book-open-variant" size={28} color={BRAND_GOLD} />
          </View>
          <Text style={gS.headerTitle}>{t('guideScreen.header.title')}</Text>
          <Text style={gS.headerSub}>{t('guideScreen.header.subtitle')}</Text>
        </View>
      </LinearGradient>

      {/* ── Scroll ── */}
      <ScrollView
        style={gS.scroll}
        contentContainerStyle={gS.scrollContent}
        showsVerticalScrollIndicator={false}
      >

        {/* Intro */}
        <View style={gS.introBox}>
          <Text style={gS.introText}>
            {t('guideScreen.intro.text')}
          </Text>
          <View style={gS.introBadgesRow}>
            <View style={gS.introBadge}>
              <Ionicons name="person-outline" size={12} color={TEXT_SUB} />
              <Text style={gS.introBadgeText}>{t('guideScreen.intro.allMembers')}</Text>
            </View>
            <View style={[gS.introBadge, { borderColor: BRAND_GOLD + '44', backgroundColor: BRAND_GOLD + '11' }]}>
              <Ionicons name="star" size={12} color={BRAND_GOLD} />
              <Text style={[gS.introBadgeText, { color: BRAND_GOLD }]}>{t('guideScreen.intro.proFeatures')}</Text>
            </View>
          </View>
        </View>

        {/* Chapitres */}
        {CHAPTERS.map(chapter => (
          <AnimCard
            key={chapter.id}
            chapter={chapter}
            isOpen={openId === chapter.id}
            onToggle={() => toggle(chapter.id)}
            t={t}
          />
        ))}

        <View style={{ height: 80 }} />
      </ScrollView>

    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const gS = StyleSheet.create({

  root:  { flex: 1, backgroundColor: DARK_BG },
  scroll:{ flex: 1 },
  scrollContent: { paddingHorizontal: 16, paddingTop: 12, paddingBottom: 40 },

  // Header
  header: {
    paddingTop:        Platform.OS === 'ios' ? 54 : 36,
    paddingBottom:     24,
    alignItems:        'center',
    position:          'relative',
  },
  backBtn: {
    position:          'absolute',
    top:               Platform.OS === 'ios' ? 52 : 36,
    left:              20,
    width:             40,
    height:            40,
    borderRadius:      12,
    backgroundColor:   'rgba(255,255,255,0.15)',
    justifyContent:    'center',
    alignItems:        'center',
  },
  headerCenter: { alignItems: 'center', gap: 6 },
  headerIconBox: {
    width:           56,
    height:          56,
    borderRadius:    18,
    backgroundColor: 'rgba(255,215,0,0.15)',
    borderWidth:     1,
    borderColor:     'rgba(255,215,0,0.3)',
    justifyContent:  'center',
    alignItems:      'center',
    marginBottom:    4,
  },
  headerTitle: { fontSize: 20, fontWeight: '800', color: '#fff', letterSpacing: 0.3 },
  headerSub:   { fontSize: 13, color: 'rgba(255,255,255,0.7)' },

  // Intro
  introBox: {
    backgroundColor:  CARD_BG,
    borderRadius:     16,
    padding:          16,
    borderWidth:      1,
    borderColor:      CARD_BORDER,
    marginBottom:     14,
    gap:              12,
  },
  introText:        { fontSize: 14, color: TEXT_SUB, lineHeight: 21 },
  introBadgesRow:   { flexDirection: 'row', gap: 10 },
  introBadge:       {
    flexDirection:    'row',
    alignItems:       'center',
    gap:              5,
    paddingHorizontal: 10,
    paddingVertical:  5,
    borderRadius:     20,
    backgroundColor:  CARD_BORDER + '44',
    borderWidth:      1,
    borderColor:      CARD_BORDER,
  },
  introBadgeText: { fontSize: 11, fontWeight: '600', color: TEXT_SUB },

  // Card accordéon
  card: {
    backgroundColor:  CARD_BG,
    borderRadius:     18,
    borderWidth:      1,
    marginBottom:     12,
    overflow:         'hidden',
    shadowColor:      '#000',
    shadowOffset:     { width: 0, height: 4 },
    shadowOpacity:    0.2,
    shadowRadius:     8,
    elevation:        4,
  },
  cardHeader: {
    flexDirection:   'row',
    alignItems:      'center',
    paddingVertical: 14,
    paddingHorizontal: 14,
    gap:             12,
  },
  chapterIconBox: {
    width:          42,
    height:         42,
    borderRadius:   13,
    justifyContent: 'center',
    alignItems:     'center',
    flexShrink:     0,
  },
  chapterTitle: {
    flex:       1,
    fontSize:   15,
    fontWeight: '700',
    color:      TEXT_MAIN,
    letterSpacing: 0.2,
  },
  stepCount: {
    paddingHorizontal: 9,
    paddingVertical:   4,
    borderRadius:      12,
    borderWidth:       1,
    flexShrink:        0,
  },
  stepCountText: { fontSize: 12, fontWeight: '800' },

  // Steps
  stepsWrap: {
    paddingHorizontal: 14,
    paddingBottom:     18,
    paddingTop:        4,
    borderTopWidth:    1,
    borderTopColor:    CARD_BORDER,
  },
  stepRow: {
    flexDirection: 'row',
    gap:           12,
    marginTop:     16,
  },

  // Timeline
  timelineCol:  { alignItems: 'center', width: 28, flexShrink: 0 },
  timelineDot:  {
    width:          28,
    height:         28,
    borderRadius:   14,
    justifyContent: 'center',
    alignItems:     'center',
  },
  timelineNum:  { color: '#fff', fontSize: 11, fontWeight: '800' },
  timelineLine: { flex: 1, width: 2, borderRadius: 1, marginTop: 4, minHeight: 16 },

  // Contenu step
  stepContent:  { flex: 1, paddingTop: 2 },
  stepTitleRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 5 },
  stepTitle:    { fontSize: 14, fontWeight: '700', color: TEXT_MAIN, flex: 1 },
  stepDesc:     { fontSize: 13, color: TEXT_SUB, lineHeight: 20 },

  // Badge PRO
  proBadge: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               3,
    backgroundColor:   BRAND_GOLD + '18',
    borderRadius:      8,
    paddingHorizontal: 6,
    paddingVertical:   2,
    borderWidth:       1,
    borderColor:       BRAND_GOLD + '44',
    flexShrink:        0,
  },
  proBadgeText: { fontSize: 9, fontWeight: '800', color: BRAND_GOLD },

});
